// ============================================================
// BATTLEFIELD PROJECTION - world metres to screen pixels.
//
// Orthographic, because a tactical map is a measuring instrument: two
// tokens the same distance apart must LOOK the same distance apart
// wherever they stand, and parallel walls must stay parallel. Perspective
// would make the map prettier and the readings a lie.
//
// THE DISPLAY MAY ASK FOR PERSPECTIVE (`Camera.fov`, ADR 2026-10-07): its
// over-the-shoulder shot needs it. The orthographic numbers below are then
// scaled by k = D / (D − toward), D the eye's distance from the target,
// and the inverse onto a plane stays closed-form. Without `fov` nothing
// here changes, to the last bit.
//
//   screenX = x·cosθ − z·sinθ
//   towards = x·sinθ + z·cosθ        (grows towards the camera)
//   screenY = towards·sinφ − y·cosφ
//   depth   = −towards               (grows away from it: the sort key)
//
// The whole transform is linear in (x, y, z), which buys two things: face
// order can be decided by comparing depths, and dragging on a horizontal
// plane inverts EXACTLY through a 2×2 matrix - no raycasting, no
// iteration, no drift over a long drag.
//
// Pure. Zero React dependency.
// ============================================================
import type { Vec3 } from './types';
import type { Boundary, Vec2 } from './boundary';
import { boundaryOutline, boundaryCentre } from './boundary';

const DEG = Math.PI / 180;

/** The three-quarter view: enough angle to read height, enough plan to read position. */
export const DEFAULT_YAW = 45;
export const DEFAULT_PITCH = 30;

/** Below this the ground vanishes to a line; at 90° the map is a plan. */
export const MIN_PITCH = 15;
export const MAX_PITCH = 90;
const MIN_ZOOM = 2;
const MAX_ZOOM = 400;

export interface Camera {
  /** Degrees about the vertical axis. */
  yaw: number;
  /** Degrees above the ground plane. 90 looks straight down. */
  pitch: number;
  /** Pixels per metre. */
  zoom: number;
  /** The ground point held at the centre of the viewport. */
  target: Vec2;
  /**
   * Vertical field of view in degrees: perspective, for the display only.
   * Absent (or 0) is the orthographic camera every other caller uses.
   */
  fov?: number;
  /**
   * How far above the ground the held point sits, in metres: the display's
   * shots look at a figure's middle, or up at one on a roof, rather than at
   * the floor under it. Absent is the ground, as every other caller has it.
   */
  lift?: number;
}

export interface Viewport {
  width: number;
  height: number;
}

/**
 * The projection's fixed coordinate space.
 *
 * The map's SVG uses this as its viewBox and lets the browser scale it to
 * whatever the panel is, so the projection never has to know the panel's
 * pixel size. No ResizeObserver, no layout dependency, and the same
 * numbers in a unit test as in a browser.
 */
export const SCENE_VIEW: Viewport = { width: 1000, height: 700 };

export interface ScreenPoint {
  x: number;
  y: number;
  /**
   * Distance from the viewer: LARGER IS FURTHER AWAY, so the painter's
   * algorithm draws in descending order. Note that this is the negation
   * of the `x·sinθ + z·cosθ` term inside the screen-Y formula, which
   * grows towards the camera - getting that sign backwards would reverse
   * the whole scene, so the two are kept deliberately distinct.
   */
  depth: number;
  /**
   * Under perspective only: how much nearer than the target this point is
   * drawn (>1 nearer, <1 further), for sizing what stands there. Absent
   * under the orthographic camera, whose scale is always 1.
   */
  scale?: number;
  /**
   * Under perspective only: the point is behind the eye or too near it to
   * draw. Its pixel is a clamped stand-in, never to be drawn: clip what
   * crosses (`clipToEye`) and leave out what does not.
   */
  behind?: true;
}

/**
 * A depth that a flat face interpolates LINEARLY across the screen, in the
 * same order as `depth` (larger is further): the depth itself under the
 * orthographic camera; under perspective minus the scale, because the scale
 * is the eye distance over the point's, and it is the reciprocal of distance
 * that runs linearly across a pinhole's image.
 */
export const depthKey = (p: { depth: number; scale?: number }) => (p.scale === undefined ? p.depth : -p.scale);

export function makeCamera(overrides: Partial<Camera> = {}): Camera {
  return {
    yaw: DEFAULT_YAW,
    pitch: DEFAULT_PITCH,
    zoom: 20,
    target: { x: 0, z: 0 },
    ...overrides,
    ...(overrides.target ? { target: { ...overrides.target } } : {}),
  };
}

/**
 * The eye's distance from the target in metres, or null for the orthographic
 * camera. Set by the field of view and the zoom together, so zooming in
 * keeps the same strength of perspective rather than flattening it.
 */
function eyeDistance(camera: Camera, view: Viewport): number | null {
  if (!camera.fov) return null;
  return view.height / camera.zoom / 2 / Math.tan((camera.fov * DEG) / 2);
}

/** Nearest the eye a point may come and be drawn: a tenth of the way to the target. */
const NEAR = 0.1;

/** How far along the line of sight, from the target towards the eye, a point lies (height counts). */
function alongSight(p: Vec3, camera: Camera): number {
  const t = camera.yaw * DEG;
  const f = camera.pitch * DEG;
  const towards = (p.x - camera.target.x) * Math.sin(t) + (p.z - camera.target.z) * Math.cos(t);
  return towards * Math.cos(f) + (p.y - (camera.lift ?? 0)) * Math.sin(f);
}

/** Where the eye stands under perspective: `eyeDistance` back from the target along the line of sight. */
export function eyePosition(camera: Camera, view: Viewport): Vec3 | null {
  const d = eyeDistance(camera, view);
  if (d === null) return null;
  const t = camera.yaw * DEG;
  const f = camera.pitch * DEG;
  return {
    x: camera.target.x + d * Math.sin(t) * Math.cos(f),
    y: (camera.lift ?? 0) + d * Math.sin(f),
    z: camera.target.z + d * Math.cos(t) * Math.cos(f),
  };
}

/** How far in front of the eye's near plane a point lies (negative: behind it); null under the orthographic camera. */
function roomBeforeEye(camera: Camera, view: Viewport): ((p: Vec3) => number) | null {
  const d = eyeDistance(camera, view);
  return d === null ? null : (p) => d - alongSight(p, camera) - d * NEAR;
}

/**
 * A LINE cut at the eye's near plane, then projected; null when it lies wholly behind. The floor, the grid, the
 * arena's edge and the terrain's edges are lines - projected point by point, an end behind the eye landed about
 * 1e5 px away, and up close the whole map blinked as those lines swung across the screen (the table, 2026-10-28).
 */
export function projectSegment(
  a: Vec3,
  b: Vec3,
  camera: Camera,
  view: Viewport,
): { a: ScreenPoint; b: ScreenPoint } | null {
  const room = roomBeforeEye(camera, view);
  const ra = room ? room(a) : 0;
  const rb = room ? room(b) : 0;
  if (ra < 0 && rb < 0) return null;
  const cut = (inFront: Vec3, behind: Vec3, rf: number, rbh: number) => lerp(inFront, behind, rf / (rf - rbh));
  const from = ra < 0 ? cut(b, a, rb, ra) : a;
  const to = rb < 0 ? cut(a, b, ra, rb) : b;
  return { a: project(from, camera, view), b: project(to, camera, view) };
}

/**
 * A polygon cut to what lies in front of the eye's near plane (one
 * Sutherland-Hodgman pass), so a solid around the camera is drawn up to the
 * eye rather than at a clamped size. The same polygon when nothing crosses,
 * and always under the orthographic camera; empty when it is all behind.
 */
export function clipToEye(points: readonly Vec3[], camera: Camera, view: Viewport): readonly Vec3[] {
  const room = roomBeforeEye(camera, view);
  if (!room || points.every((p) => room(p) >= 0)) return points;
  const out: Vec3[] = [];
  points.forEach((p, i) => {
    const q = points[(i + 1) % points.length];
    const a = room(p);
    const b = room(q);
    if (a >= 0) out.push(p);
    if (a >= 0 !== b >= 0) out.push(lerp(p, q, a / (a - b)));
  });
  return out;
}

const lerp = (p: Vec3, q: Vec3, s: number): Vec3 => ({
  x: p.x + (q.x - p.x) * s,
  y: p.y + (q.y - p.y) * s,
  z: p.z + (q.z - p.z) * s,
});

/** Project a world point to viewport pixels, carrying its depth along. */
export function project(p: Vec3, camera: Camera, view: Viewport): ScreenPoint {
  const t = camera.yaw * DEG;
  const f = camera.pitch * DEG;
  const dx = p.x - camera.target.x;
  const dz = p.z - camera.target.z;

  const u = dx * Math.cos(t) - dz * Math.sin(t);
  // Towards the camera: at yaw 0 the viewer stands at +z looking along −z,
  // so a larger `towards` is a point nearer the eye and lower on screen.
  const towards = dx * Math.sin(t) + dz * Math.cos(t);
  // Heights count from the held point's own (`lift`): the ground, unless a shot looks higher.
  const y = p.y - (camera.lift ?? 0);
  const v = towards * Math.sin(f) - y * Math.cos(f);

  const d = eyeDistance(camera, view);
  if (d === null) {
    return {
      x: view.width / 2 + u * camera.zoom,
      y: view.height / 2 + v * camera.zoom,
      depth: -towards,
    };
  }
  // Along the line of sight, so height counts: a tower top is nearer an eye above it.
  const toward = towards * Math.cos(f) + y * Math.sin(f);
  const ahead = d - toward;
  const scale = d / Math.max(d * NEAR, ahead);
  return {
    x: view.width / 2 + u * camera.zoom * scale,
    y: view.height / 2 + v * camera.zoom * scale,
    depth: -toward,
    scale,
    // A hair of slack, so a point `clipToEye` put exactly on the near plane still counts as in front.
    ...(ahead < d * NEAR * (1 - 1e-9) ? { behind: true as const } : {}),
  };
}

/**
 * Where on the horizontal plane at height `planeY` does this pixel land?
 *
 * This is the whole interaction model: a token dragged by its footprint
 * follows the pointer across the ground exactly, because the inverse is
 * closed-form rather than searched for.
 *
 * Returns null when the view is edge-on (pitch 0), where a pixel maps to
 * a whole line rather than a point.
 */
export function unprojectToPlane(
  screenX: number,
  screenY: number,
  planeY: number,
  camera: Camera,
  view: Viewport,
): Vec3 | null {
  const f = camera.pitch * DEG;
  const a = (screenX - view.width / 2) / camera.zoom;
  const b = (screenY - view.height / 2) / camera.zoom;
  const d = eyeDistance(camera, view);
  const above = planeY - (camera.lift ?? 0);
  const ground = d === null ? flatGround(a, b, above, f) : perspectiveGround(a, b, above, f, d);
  if (!ground) return null;
  const { u, depth } = ground;

  // The 2×2 rotation inverts to its own transpose - no division, no
  // conditioning worries, exact to the last bit the inputs carry.
  const t = camera.yaw * DEG;
  const dx = u * Math.cos(t) + depth * Math.sin(t);
  const dz = -u * Math.sin(t) + depth * Math.cos(t);

  return { x: camera.target.x + dx, y: planeY, z: camera.target.z + dz };
}

/** The orthographic inverse: across (u) and towards (depth) on the plane. */
function flatGround(a: number, b: number, planeY: number, f: number): { u: number; depth: number } | null {
  const sinF = Math.sin(f);
  if (Math.abs(sinF) < 1e-9) return null;
  return { u: a, depth: (b + planeY * Math.cos(f)) / sinF };
}

/**
 * The perspective inverse. With b = v·k and k = D / (D − towards·cosφ − y·sinφ),
 * `towards` solves linearly; a ray that meets the plane behind the eye (or
 * never) is refused, as the orthographic edge-on case is.
 */
function perspectiveGround(a: number, b: number, y: number, f: number, d: number): { u: number; depth: number } | null {
  const sinF = Math.sin(f);
  const cosF = Math.cos(f);
  const denom = d * sinF + b * cosF;
  if (Math.abs(denom) < 1e-9) return null;
  const towards = (b * d - b * y * sinF + d * y * cosF) / denom;
  const ahead = d - towards * cosF - y * sinF;
  if (ahead <= d * NEAR) return null;
  return { u: (a * ahead) / d, depth: towards };
}

export const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

/** The screen's right on the ground at this yaw (`project`'s u axis), a unit vector. */
export const screenRight = (yawDeg: number) => ({ x: Math.cos(yawDeg * DEG), z: -Math.sin(yawDeg * DEG) });

/** Turn the camera. Returns a NEW camera; the old one is never touched. */
export function orbit(camera: Camera, deltaYaw: number, deltaPitch: number): Camera {
  return {
    ...camera,
    target: { ...camera.target },
    yaw: (((camera.yaw + deltaYaw) % 360) + 360) % 360,
    pitch: clamp(camera.pitch + deltaPitch, MIN_PITCH, MAX_PITCH),
  };
}

/** Multiply the zoom, clamped. Returns a NEW camera. */
export function zoomBy(camera: Camera, factor: number): Camera {
  return {
    ...camera,
    target: { ...camera.target },
    zoom: clamp(camera.zoom * factor, MIN_ZOOM, MAX_ZOOM),
  };
}

/** Slide the camera across the ground. Returns a NEW camera. */
export function panBy(camera: Camera, dx: number, dz: number): Camera {
  return { ...camera, target: { x: camera.target.x + dx, z: camera.target.z + dz } };
}

/** A little air around the arena, so the edge is not flush with the frame. */
const FIT_MARGIN = 0.9;

/**
 * Frame the whole arena: centred on it, zoomed so it fits.
 *
 * Computed from the PROJECTED outline rather than from the world extent,
 * because at a three-quarter angle a long arena covers far less screen
 * width than its length suggests - and because the fit then depends on
 * the angles, which is why they are a parameter.
 *
 * Reset View passes nothing and gets the default three-quarter view. A
 * re-frame after loading a map passes the CURRENT angles, so that
 * arriving terrain does not spin the camera out from under someone who
 * is building top-down.
 */
export function fitCamera(boundary: Boundary, view: Viewport, angles: { yaw?: number; pitch?: number } = {}): Camera {
  const yaw = angles.yaw ?? DEFAULT_YAW;
  const pitch = angles.pitch ?? DEFAULT_PITCH;
  const centre = boundaryCentre(boundary);
  const base = makeCamera({ yaw, pitch, zoom: 1, target: centre });

  let maxU = 1e-6;
  let maxV = 1e-6;
  for (const p of boundaryOutline(boundary)) {
    const s = project(p, base, { width: 0, height: 0 });
    maxU = Math.max(maxU, Math.abs(s.x));
    maxV = Math.max(maxV, Math.abs(s.y));
  }

  const zoom = clamp(Math.min(view.width / (2 * maxU), view.height / (2 * maxV)) * FIT_MARGIN, MIN_ZOOM, MAX_ZOOM);
  return makeCamera({ yaw, pitch, zoom, target: centre });
}

/** Camera presets offered in the toolbar. */
export const CAMERA_PRESETS = [
  { key: 'threeQuarter', label: 'Three-quarter', yaw: DEFAULT_YAW, pitch: DEFAULT_PITCH },
  // Painter's algorithm is exact from directly above: no face can hide
  // another. This is the view to reach for when an overlap looks wrong.
  { key: 'tactical', label: 'Tactical', yaw: 0, pitch: 90 },
  { key: 'low', label: 'Low angle', yaw: DEFAULT_YAW, pitch: MIN_PITCH },
] as const;

/** A point on the ground (y = 0) on the scene's screen: the one projection the display's ground marks share. */
export const projectGround = (p: { x: number; z: number }, camera: Camera): ScreenPoint =>
  project({ x: p.x, y: 0, z: p.z }, camera, SCENE_VIEW);
